import { useEffect, useRef, useState } from 'react';

import { v1 } from 'uuid';

import { ApiStatus } from '../constants/apiStatus';

type RequestDelegate<T> = () => Promise<T>;
type FetchDataFunction<T> = (request?: RequestDelegate<T>) => void;

export const useFetchData = <T>(): [
  ApiStatus,
  T | undefined,
  FetchDataFunction<T>,
  () => void
] => {
  const [apiStatus, setApiStatus] = useState(ApiStatus.loading);
  const [data, setData] = useState<T>();

  // Flag indicates whether component was unmounted
  const isUnmountedRef = useRef<boolean>(false);
  // Unique identifier of the last request
  const requestIdRef = useRef<string>();

  // Request delegate stored between renders by using `useRef`
  const requestDelegateRef = useRef<RequestDelegate<T>>();

  // 'Fetch data' function with ability to specify request delegate
  const fetchDataRef = useRef<FetchDataFunction<T>>(
    (requestDelegate?: RequestDelegate<T>) => {
      if (requestDelegate) requestDelegateRef.current = requestDelegate;

      if (!requestDelegateRef.current) return;

      const guid = v1();
      requestIdRef.current = guid;

      setApiStatus(ApiStatus.loading);
      requestDelegateRef
        .current()
        .then(data => {
          if (isUnmountedRef.current || guid !== requestIdRef.current) return;

          setData(data);
          setApiStatus(ApiStatus.success);
        })
        .catch(e => {
          console.error(e);

          if (isUnmountedRef.current || guid !== requestIdRef.current) return;

          setApiStatus(ApiStatus.error);
        });
    }
  );

  // 'Fetch data' with the last request delegate
  const reFetchDataRef = useRef<() => void>(() => fetchDataRef.current());

  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  return [apiStatus, data, fetchDataRef.current, reFetchDataRef.current];
};
